import type { APIError } from '@vizit/api';
import { useGetRetailersForOrganizationQuery } from '@vizit/api';
import { ButtonSize, ToggleButton, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { BeakerIcon } from '@vizit/icons';
import {
  BrandComboBox,
  ProductCategoryComboBox,
  RetailerSelect,
  ScoreLevelSelect,
  SearchTextField,
} from '@vizit/organisms';
import { cx } from '@vizit/theme';
import React from 'react';
import { useProductTable } from '../../providers';
import { useBrandList, useProductCategoryList } from './product-table-filters.hooks';

/**
 * Enum representing the available filter options for the product table
 */
enum ProductTableFilterOptions {
  ListingScoreLevel = 'score',
  Brand = 'brand',
  ProductCategory = 'categoryId',
  ProductCategoryName = 'categoryName',
  Search = 'searchTerm',
  Retailer = 'retailer',
  Experiment = 'experiment',
}

/**
 * Shared min-width for the filter picker wrappers, per the 1440 dev-handoff spec.
 * Kept in one place so the value can't drift across the four controls.
 */
const FILTER_MIN_WIDTH = 'min-w-[233px]';

interface ProductTableFiltersProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Function to handle authentication error
   *
   * @type {(error: APIError) => void}
   * @param error - The authentication error
   */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Component that renders filter controls for the product table.
 * Supports filtering by product category and listing score level.
 * Uses ProductTableProvider context for state management.
 *
 * @param props - Component props
 * @returns JSX element
 */
function ProductTableFilters(props: ProductTableFiltersProps) {
  const { className, onAuthenticationError, ...other } = props;

  const {
    organizationId,
    workspaceId,
    listingScoreLevel,
    productCategoryId,
    productCategoryName,
    brand,
    searchTerm,
    retailer,
    experimentOnly,
    onFilterExperiment,
    onFilterListingScoreLevel,
    onFilterProductCategory,
    onFilterBrand,
    onSearchTermChange,
    onFilterRetailer,
  } = useProductTable();

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const { data: retailers } = useGetRetailersForOrganizationQuery(
    { org_id: organizationId ?? '' },
    { enabled: isRetailerEnabled && !!organizationId },
  );

  const {
    items: productCategoryItems,
    isLoading: isLoadingProductCategory,
    filterText: productCategoryFilterText,
    setFilterText: setProductCategoryFilterText,
    loadMore: loadMoreProductCategory,
    onSelectionChange: onCategorySelectionChange,
  } = useProductCategoryList({
    organizationId,
    workspaceId,
    filterText: productCategoryName || '',
    onAuthenticationError,
    onFilterProductCategory,
  });

  const {
    items: brandItems,
    isLoading: isLoadingBrand,
    filterText: brandFilterText,
    setFilterText: setBrandFilterText,
    loadMore: loadMoreBrand,
    onSelectionChange: onBrandSelectionChange,
  } = useBrandList({
    workspaceId: workspaceId,
    filterText: brand || '',
    onAuthenticationError: onAuthenticationError,
    onFilterBrand,
  });

  const brandSelectedKey = React.useMemo(
    () => (brand ? brand.trim().toLowerCase().replace('-', ' ') : undefined),
    [brand],
  );

  return (
    <div className={cx('flex flex-row flex-wrap gap-6 pb-8', className)} {...other}>
      <SearchTextField
        className="min-w-[300px] max-w-[600px]"
        onSearch={onSearchTermChange}
        defaultValue={searchTerm ?? ''}
        variant="filled"
        placeholder="Search pages by title or ID"
        data-testid="library-products-search"
      />
      {isRetailerEnabled && retailers && retailers.length > 1 && (
        <RetailerSelect
          className={FILTER_MIN_WIDTH}
          data-testid="library-products-retailer-filter"
          retailers={retailers}
          selectedRetailer={retailer}
          onRetailerChange={onFilterRetailer}
        />
      )}
      <ProductCategoryComboBox
        className={FILTER_MIN_WIDTH}
        data-testid="library-products-category-filter"
        filterText={productCategoryFilterText}
        setFilterText={setProductCategoryFilterText}
        items={productCategoryItems}
        selectedKey={productCategoryId}
        onSelectionChange={onCategorySelectionChange}
        isLoading={isLoadingProductCategory}
        loadMore={loadMoreProductCategory}
      />
      <BrandComboBox
        className={FILTER_MIN_WIDTH}
        data-testid="library-products-brand-filter"
        selectedKey={brandSelectedKey}
        filterText={brandFilterText}
        setFilterText={setBrandFilterText}
        items={brandItems}
        onSelectionChange={onBrandSelectionChange}
        isLoading={isLoadingBrand}
        loadMore={loadMoreBrand}
      />
      <ScoreLevelSelect
        className={FILTER_MIN_WIDTH}
        data-testid="library-products-score-filter"
        scoreLevel={listingScoreLevel}
        onScoreLevelChange={onFilterListingScoreLevel}
      />
      <TooltipTrigger delay={300}>
        <ToggleButton
          isSelected={experimentOnly ?? false}
          onChange={onFilterExperiment}
          size={ButtonSize.Md}
          className="h-auto w-20 self-stretch"
          iconStart={<BeakerIcon size={20} aria-hidden />}
          aria-label="Show experiments only"
          data-testid="library-products-experiment-filter"
        />
        <Tooltip>Show experiments only</Tooltip>
      </TooltipTrigger>
    </div>
  );
}

export { ProductTableFilters, ProductTableFilterOptions };
